// ===== BEGIN FILE: src/components/PlanetHouseSummary.tsx =====
import * as React from 'react';
import { Box, Sheet, Typography, Table, IconButton, Tooltip, Card, Chip } from '@mui/joy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import type { HouseInfluenceRow, HouseInfluenceEntry } from '../utils/d1HouseInfluence';

type Planet =
  | 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';

type PlanetTotals = Partial<Record<Planet, {
  svPct?: number;
  shadbalaPct?: number;
  ishta?: number;
  kashta?: number;
  blendedPct?: number;
  bhavaSandhiMultiplier?: number; // 0.80 to 1.10 (only for occupying planets in global mode)
}>>;

export type PlanetHouseSummaryProps = {
  planetTotals: PlanetTotals;
  houseRows: HouseInfluenceRow[];
  bhavaBalaRupas?: number[]; // Array of 12 values (percentage for each house, 0-100)
  /**
   * Optional color resolver used elsewhere (e.g., chart) to color planets by
   * functional role (benefic/malefic/neutral). If provided, we will color the
   * column headers with the same palette to keep visuals consistent.
   */
  getPlanetColor?: (planet: Planet) => string;
};

const PLANETS: Planet[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];

function pct(value: number | undefined) {
  return typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value)}%` : '—';
}

function renderList(entries: HouseInfluenceEntry[], getPlanetColor?: (p: Planet) => string) {
  if (!entries?.length) return <span>—</span>;
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
      {entries.map((entry, idx) => {
        const color = getPlanetColor ? getPlanetColor(entry.planet as Planet) : undefined;
        return (
          <span key={`${entry.planet}-${idx}`}>
            <span style={{ color, fontWeight: 600 }}>{entry.planet}</span> {Math.round(entry.pct)}%
            {idx < entries.length - 1 ? ',' : ''}
          </span>
        );
      })}
    </span>
  );
}

export default function PlanetHouseSummary({ planetTotals, houseRows, bhavaBalaRupas, getPlanetColor }: PlanetHouseSummaryProps) {
  React.useEffect(() => {
    try {
      console.log('[PlanetHouseSummary] houseRows update', {
        count: houseRows.length,
        sample: houseRows.slice(0, 2),
      });
    } catch {
      // ignore logging issues
    }
  }, [houseRows]);

  return (
    <Box sx={{ display: 'grid', gap: 2, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Planet factors */}
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Typography level="h4" sx={{ fontWeight: 600 }}>
            Planet Factors (row = factor, columns = planets)
          </Typography>
          <Tooltip title="Detailed breakdown of each planet's strength from different angles. Shodashavarga shows divisional chart scores, Shadbala shows six types of strength, Ishta/Kashta shows good vs bad points, and Blended total combines everything into one final score for each planet." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
            <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 18, minHeight: 18, padding: '2px' }}>
              <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Mobile Card View */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5, width: '100%', minWidth: 0 }}>
          {PLANETS.map(planet => {
            const color = typeof getPlanetColor === 'function' ? getPlanetColor(planet) : undefined;
            const entry = planetTotals[planet];
            return (
              <Card key={planet} variant="outlined" sx={{ p: 1.5, width: '100%', minWidth: 0, overflow: 'hidden' }}>
                <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600, color: color || 'text.primary' }}>
                  {planet}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, fontSize: '0.875rem', minWidth: 0 }}>
                  <Box>
                    <Typography level="body-xs" sx={{ opacity: 0.7 }}>Shodashvarga</Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>{pct(entry?.svPct)}</Typography>
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ opacity: 0.7 }}>Shadbala</Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>{pct(entry?.shadbalaPct)}</Typography>
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ opacity: 0.7 }}>Ishta</Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 600, color: 'success.600' }}>
                      {entry?.ishta != null ? Math.round(entry.ishta) : '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ opacity: 0.7 }}>Kashta</Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 600, color: 'danger.500' }}>
                      {entry?.kashta != null ? Math.round(entry.kashta) : '-'}
                    </Typography>
                  </Box>
                  {entry?.bhavaSandhiMultiplier != null && (
                    <Box sx={{ gridColumn: '1 / -1', bgcolor: '#fff9e6', p: 0.5, borderRadius: 'sm', mt: 0.5 }}>
                      <Typography level="body-xs" sx={{ opacity: 0.7 }}>Bhava Sandhi × (cusp proximity)</Typography>
                      <Typography 
                        level="body-sm" 
                        sx={{ 
                          fontWeight: 600,
                          color: entry.bhavaSandhiMultiplier < 1.0 ? 'warning.600' : 
                                 entry.bhavaSandhiMultiplier > 1.0 ? 'success.600' : 'text.secondary'
                        }}
                      >
                        {entry.bhavaSandhiMultiplier.toFixed(2)}×
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography level="body-xs" sx={{ opacity: 0.7 }}>Blended Total</Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 700, fontSize: '1rem' }}>{pct(entry?.blendedPct)}</Typography>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>

        {/* Desktop Table View */}
        <Sheet variant="outlined" sx={{ display: { xs: 'none', sm: 'block' }, overflowX: 'auto', borderRadius: 'sm' }}>
          <Table size="sm" stickyHeader sx={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>
                  <Typography level="body-sm" sx={{ fontWeight: 600 }}>Factor / Planet</Typography>
                </th>
                {PLANETS.map(planet => {
                  const color = typeof getPlanetColor === 'function' ? getPlanetColor(planet) : undefined;
                  return (
                    <th key={planet} title={planet} style={{ textAlign: 'center' }}>
                      <Typography level="body-sm" sx={{ fontWeight: 600, color: color || 'text.primary' }}>
                        {planet}
                      </Typography>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><Typography level="body-sm">Total Shodashvarga %</Typography></td>
                {PLANETS.map(planet => (
                  <td key={planet}><Typography level="body-sm" sx={{ textAlign: 'center' }}>{pct(planetTotals[planet]?.svPct)}</Typography></td>
                ))}
              </tr>
              <tr>
                <td><Typography level="body-sm">Total Shadbala %</Typography></td>
                {PLANETS.map(planet => (
                  <td key={planet}><Typography level="body-sm" sx={{ textAlign: 'center' }}>{pct(planetTotals[planet]?.shadbalaPct)}</Typography></td>
                ))}
              </tr>
              <tr>
                <td><Typography level="body-sm">Ishta / Kashta (0-60)</Typography></td>
                {PLANETS.map(planet => {
                  const entry = planetTotals[planet];
                  if (entry?.ishta == null && entry?.kashta == null) return <td key={planet}><Typography level="body-sm" sx={{ textAlign: 'center' }}>-</Typography></td>;
                  const ishta = Math.round(entry?.ishta ?? 0);
                  const kashta = Math.round(entry?.kashta ?? 0);
                  return (
                    <td key={planet}>
                      <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                        <Typography sx={{ color: 'success.600', fontWeight: 600 }}>{ishta}</Typography>
                        {' / '}
                        <Typography sx={{ color: 'danger.500', fontWeight: 600 }}>{kashta}</Typography>
                      </Typography>
                    </td>
                  );
                })}
              </tr>
              {/* Bhava Sandhi row (only show if any planet has multiplier) */}
              {PLANETS.some(p => planetTotals[p]?.bhavaSandhiMultiplier != null) && (
                <tr style={{ background: '#fff9e6' }}>
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      Bhava Sandhi × (cusp proximity)
                    </Typography>
                  </td>
                  {PLANETS.map(planet => {
                    const mult = planetTotals[planet]?.bhavaSandhiMultiplier;
                    if (mult == null) return <td key={planet}><Typography level="body-sm" sx={{ textAlign: 'center' }}>-</Typography></td>;
                    const color = mult < 1.0 ? 'warning.600' : mult > 1.0 ? 'success.600' : 'text.secondary';
                    return (
                      <td key={planet}>
                        <Typography level="body-sm" sx={{ textAlign: 'center', fontWeight: 600, color }}>
                          {mult.toFixed(2)}×
                        </Typography>
                      </td>
                    );
                  })}
                </tr>
              )}
              <tr>
                <td><Typography level="body-sm">Blended total %</Typography></td>
                {PLANETS.map(planet => (
                  <td key={planet}>
                    <Typography level="body-sm" sx={{ fontWeight: 600, textAlign: 'center' }}>
                      {pct(planetTotals[planet]?.blendedPct)}
                    </Typography>
                  </td>
                ))}
              </tr>
            </tbody>
          </Table>
        </Sheet>
      </Box>

      {/* House-wise influence */}
      <Box id="section-d1-house-scores" sx={{ width: '100%', maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Typography level="h4" sx={{ fontWeight: 600 }}>
            D1 House Scores (blended planetary influence)
          </Typography>
          <Tooltip title="Shows how strong each life area (house) is based on house lord strength, natural house power (Bhava Bala), planets sitting in that house, and planets aspecting that house. Higher positive scores = better results in that life area." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
            <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 18, minHeight: 18, padding: '2px' }}>
              <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Mobile Card View */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5, width: '100%', minWidth: 0 }}>
          {houseRows.map((row, idx) => {
            const lordColor = typeof getPlanetColor === 'function' ? getPlanetColor(row.lord as Planet) : undefined;
            return (
              <Card key={idx} variant="outlined" sx={{ p: 1.5, width: '100%', minWidth: 0, overflow: 'hidden' }}>
                <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
                  House {row.house}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, fontSize: '0.875rem' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ opacity: 0.7 }}>Lord</Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 600, color: lordColor, wordBreak: 'break-word' }}>{row.lord}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ opacity: 0.7 }}>Lord Strength</Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>{row.lordStrength ? `${Math.round(row.lordStrength)}%` : '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ opacity: 0.7 }}>Bhava Bala</Typography>
                    <Typography level="body-sm">{bhavaBalaRupas && bhavaBalaRupas[row.house - 1] != null ? `${Math.round(bhavaBalaRupas[row.house - 1])}%` : '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ opacity: 0.7 }}>Total Score</Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 700 }}>{row.total >= 0 ? `+${row.total}` : row.total}</Typography>
                  </Box>
                  {row.residents && row.residents.length > 0 && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography level="body-xs" sx={{ opacity: 0.7, mb: 0.25 }}>Residents</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {row.residents.map((res: HouseInfluenceEntry, i: number) => {
                          const resColor = typeof getPlanetColor === 'function' ? getPlanetColor(res.planet as Planet) : undefined;
                          return (
                            <Chip key={i} size="sm" sx={{ fontSize: '0.7rem', bgcolor: `${resColor}20`, color: resColor, flexShrink: 0 }}>
                              {res.planet} {Math.round(res.pct)}%
                            </Chip>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  {row.aspectors && row.aspectors.length > 0 && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography level="body-xs" sx={{ opacity: 0.7, mb: 0.25 }}>Aspectors</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {row.aspectors.map((asp: HouseInfluenceEntry, i: number) => {
                          const aspColor = typeof getPlanetColor === 'function' ? getPlanetColor(asp.planet as Planet) : undefined;
                          return (
                            <Chip key={i} size="sm" sx={{ fontSize: '0.7rem', bgcolor: `${aspColor}20`, color: aspColor, flexShrink: 0 }}>
                              {asp.planet} {Math.round(asp.pct)}%
                            </Chip>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Card>
            );
          })}
        </Box>

        {/* Desktop Table View */}
        <Sheet variant="outlined" sx={{ display: { xs: 'none', sm: 'block' }, overflowX: 'auto', borderRadius: 'sm' }}>
          <Table size="sm" stickyHeader sx={{ minWidth: 820, '& th, & td': { verticalAlign: 'top' }, '& th': { whiteSpace: 'normal' } }}>
            <thead>
              <tr>
                <th style={{ width: '50px' }}><Typography level="body-sm" sx={{ fontWeight: 600, whiteSpace: 'normal' }}>House</Typography></th>
                <th style={{ width: '70px' }}><Typography level="body-sm" sx={{ fontWeight: 600, whiteSpace: 'normal' }}>Lord</Typography></th>
                <th style={{ width: '90px' }}><Typography level="body-sm" sx={{ fontWeight: 600, whiteSpace: 'normal' }}>Lord Strength</Typography></th>
                <th style={{ width: '90px' }}><Typography level="body-sm" sx={{ fontWeight: 600, whiteSpace: 'normal' }}>Bhava Bala</Typography></th>
                <th style={{ width: '150px' }}><Typography level="body-sm" sx={{ fontWeight: 600, whiteSpace: 'normal' }}>Resident Planets (weighted)</Typography></th>
                <th style={{ width: '150px' }}><Typography level="body-sm" sx={{ fontWeight: 600, whiteSpace: 'normal' }}>Aspecting Planets (weighted)</Typography></th>
                <th style={{ width: '90px' }}><Typography level="body-sm" sx={{ fontWeight: 600, whiteSpace: 'normal' }}>House Total</Typography></th>
                <th style={{ width: 'auto' }}><Typography level="body-sm" sx={{ fontWeight: 600, whiteSpace: 'normal' }}>Activities to balance the House</Typography></th>
              </tr>
            </thead>
            <tbody>
              {houseRows.map((row, idx) => (
                <tr key={row.house}>
                  <td><Typography level="body-sm" sx={{ textAlign: 'center' }}>{row.house}</Typography></td>
                  <td><Typography level="body-sm" sx={{ textAlign: 'center' }}>{row.lord}</Typography></td>
                  <td><Typography level="body-sm" sx={{ textAlign: 'center' }}>{pct(row.lordStrength)}</Typography></td>
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 600, color: 'primary.500', textAlign: 'center' }}>
                      {bhavaBalaRupas?.[idx] != null ? `${Math.round(bhavaBalaRupas[idx])}%` : '—'}
                    </Typography>
                  </td>
                  <td><Typography level="body-sm">{renderList(row.residents, getPlanetColor)}</Typography></td>
                  <td><Typography level="body-sm">{renderList(row.aspectors, getPlanetColor)}</Typography></td>
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 600, textAlign: 'center' }}>
                      {Math.round(row.total)}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-xs" sx={{ whiteSpace: 'normal' }}>
                      {row.activities || '—'}
                    </Typography>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
        <Typography level="body-xs" sx={{ mt: 0.75, color: 'text.tertiary', wordBreak: 'break-word', whiteSpace: 'normal' }}>
          Note: weights shown are signed by functional role (benefic +, malefic −). Aspect weight is 1.0 when the house is empty, 0.5 when occupied.
        </Typography>
      </Box>
    </Box>
  );
}
// ===== END FILE: src/components/PlanetHouseSummary.tsx =====
